import { i18n } from '../../i18n/i18n'

import React, { Component, Fragment } from 'react'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import { connect, ConnectedProps } from 'react-redux'
import { isEmpty, keys, last, map } from 'lodash'
import { RootState } from '../../store'
import confirmService from '../../services/confirm.service'
import printService from '../../services/print.service'
import customLabelApi, {
  CustomLabelData,
  CustomLabelReprintPrintsRequest
} from '../../apis/customLabel.api'
import {
  replaceId,
  replacePage,
  replaceLimit,
  replaceFilters
} from './CustomLabelPrintsPaginate.slice'
import { paginate, deletePrints } from './CustomLabelPrintsPaginate.thunk'
import Button from '../../components/Button'
import Card from '../../components/Card'
import CardButton from '../../components/CardButton'
import { ConfirmParams } from '../../components/Confirm/Confirm.state'
import ContentLayout from '../../components/ContentLayout'
import ContentMain from '../../components/ContentMain'
import PageHeading from '../../components/PageHeading'
import TableHorizontal, { TableHorizontalHeader } from '../../components/TableHorizontal'
import CustomLabelInfo from '../../partials/CustomLabelInfo'
import { WatermarkHelper } from '../../components/WatermarkHelper'
import ResultLimiter from '../../components/ResultLimiter'
import InputText from '../../components/InputText'
import Paginator from '../../components/Paginator'
import DateRangePicker from '../../components/DateRangePicker'
import Modal from '../../components/Modal'

interface RouteState {
    id: number
}

interface Props
    extends RouteComponentProps<any, any, RouteState>,
        ConnectedProps<typeof connector> {
}

interface State {
    customLabel: CustomLabelData | null
    /** Open when the selected runs could be reprinted at their own version. */
    isReprintChoiceOpen: boolean
    selectedIds: number[]
    filterSearch: string
    filterStartAt: string
    filterEndAt: string
}

class CustomLabelPrintsPaginate extends Component<Props, State> {
  constructor (props: Props) {
    super(props)
    this.state = {
      customLabel: null,
      isReprintChoiceOpen: false,
      selectedIds: [],
      filterSearch: props.search,
      filterStartAt: props.startAt,
      filterEndAt: props.endAt
    }
  }

  get id () {
    return this.props.location.state.id
  }

  get headers (): TableHorizontalHeader[] {
    const customLabelPrint = last(this.props.data)
    if (!customLabelPrint) return []
    const { attributes } = customLabelPrint
    const headers: TableHorizontalHeader[] = map(keys(attributes), key => {
      return { heading: key, path: `attributes.${key}` }
    })
    // Only shown for labels that track versions, so nothing changes for the rest.
    const versionHeader: TableHorizontalHeader[] = customLabelPrint.tracksVersions
      ? [{ heading: i18n('Version'), path: 'versionLabel' }]
      : []
    return [
      { heading: i18n('Number'), path: 'cuid' },
      ...versionHeader,
      ...headers
    ]
  }

  get heading () {
    const { customLabel } = this.state
    const nameText = customLabel ? `: ${customLabel.name}` : ''
    return `${i18n('Prints')} ${nameText}`
  }

  get selectedActions () {
    return !isEmpty(this.state.selectedIds) &&
            <Fragment>
                <Button
                    text={i18n('Delete selected')}
                    onClick={this.handleDeleteSelectedClick}/>
                <Button
                    text={i18n('Reprint selected')}
                    onClick={this.handleReprintSelectedClick}/>
            </Fragment>
  }

  get status () {
    const { from, to, total } = this.props
    if (!from || !to) return null
    return `${i18n('ShowingX')} ${from} ${i18n('toX')} ${to} ${i18n('ofX')} ${total} ${i18n('results')}`
  }

  route = (pathname: string) => {
    const state = { id: this.id }
    this.props.history.push({ pathname, state })
  }

  handleLimitChange = (limit: string) => {
    this.props.replaceLimit(Number(limit))
  }

  handleFilterChange = (name: string, value: string) => {
    if (name === 'filterSearch') {
      this.setState({ filterSearch: value })
    }
  }

  handleDateFilterChange = (startAt: string, endAt: string) => {
    this.setState({
      filterStartAt: startAt,
      filterEndAt: endAt
    })
  }

  handleSearch = () => {
    const { filterSearch, filterStartAt, filterEndAt } = this.state
    this.props.replaceFilters(filterSearch, filterStartAt, filterEndAt)
  }

  handleResetFilters = () => {
    this.setState({
      filterSearch: '',
      filterStartAt: '',
      filterEndAt: ''
    }, () => this.props.replaceFilters('', '', ''))
  }

  handlePaginate = (page: number) => {
    this.props.replacePage(page)
  }

  handleTableSelect = (selectedIds: number[]) => {
    this.setState({ selectedIds })
  }

  handleDeleteSelectedConfirm = () => {
    const { selectedIds } = this.state
    this.props.deletePrints(selectedIds, () => {
      this.setState({ selectedIds: [] })
    })
  }

  handleDeleteSelectedClick = () => {
    const confirm: ConfirmParams = {
      id: this.id,
      type: 'warning',
      question: i18n('Are you sure to delete the custom label prints?'),
      confirmText: i18n('Yes'),
      cancelText: i18n('No'),
      onConfirm: () => this.handleDeleteSelectedConfirm()
    }
    confirmService.push(confirm)
  }

  /**
   * Runs in the selection that were printed from a recorded version — those are
   * the only ones that could be reproduced as they were.
   */
  get selectedWithVersion () {
    const { selectedIds } = this.state
    return this.props.data.filter(print =>
      selectedIds.includes(print.id) && print.versionNumber
    )
  }

  handleReprintSelectedClick = () => {
    // Nothing in the selection has a version to go back to, so there is no
    // choice to make — behave exactly as reprint always has.
    if (this.selectedWithVersion.length === 0) {
      return this.reprint(false)
    }
    this.setState({ isReprintChoiceOpen: true })
  }

  reprint = async (useOriginalVersion: boolean) => {
    const { selectedIds } = this.state
    const payload: CustomLabelReprintPrintsRequest = { useOriginalVersion }

    if (selectedIds) payload.prints = selectedIds

    this.setState({ isReprintChoiceOpen: false })

    customLabelApi.reprintPrints(this.id, payload, async (markups) => {
      if (!markups) return
      markups = await WatermarkHelper.applyWatermarks(markups)

      printService.printMarkups(markups)
    })
  }

  readCustomLabel = () => {
    customLabelApi.read(this.id, customLabel => {
      if (!customLabel) return
      this.setState({ customLabel })
    })
  }

  componentDidMount () {
    this.props.replaceId(this.id)
    this.readCustomLabel()
    this.props.paginate()
  }

  componentDidUpdate (prevProps: Props) {
    const filtersChanged = this.props.search !== prevProps.search ||
      this.props.startAt !== prevProps.startAt ||
      this.props.endAt !== prevProps.endAt

    if (filtersChanged) {
      this.setState({
        filterSearch: this.props.search,
        filterStartAt: this.props.startAt,
        filterEndAt: this.props.endAt
      })
    }
  }

  /**
   * Which design to repeat a run with. Only shown when the selection actually
   * contains runs made from a recorded version — otherwise there is nothing to
   * choose between and reprint goes straight through.
   */
  renderReprintChoice () {
    if (!this.state.isReprintChoiceOpen) return null
    const withVersion = this.selectedWithVersion
    const versions = Array.from(new Set(withVersion.map(print => `v${print.versionNumber}`))).join(', ')
    const total = this.state.selectedIds.length
    return (
      <Modal
        title={i18n('Reprint selected')}
        isOpen={true}
        onClose={() => this.setState({ isReprintChoiceOpen: false })}
        className="max-w-xl">
        <Card>
          <p className="mb-2">
            {withVersion.length} {i18n('of')} {total} {i18n('selected prints were made from an earlier version')} ({versions}).
          </p>
          <p className="text-sm text-secondary mb-4">
            {i18n('Choose which design to print them with. Prints without a recorded version always use the current design.')}
          </p>
          <div className="flex justify-end gap-3">
            <Button
              text={i18n('Cancel')}
              type="secondary"
              onClick={() => this.setState({ isReprintChoiceOpen: false })}/>
            <Button
              text={i18n('Original version')}
              type="secondary"
              onClick={() => this.reprint(true)}/>
            <Button
              text={i18n('Current design')}
              onClick={() => this.reprint(false)}/>
          </div>
        </Card>
      </Modal>
    )
  }

  render () {
    return (
            <ContentLayout>
                <ContentMain layout="twoThird">
                    <Card>
                        <PageHeading text={this.heading}/>
                        <div className="block">
                            <div className="mb-3">
                                <ResultLimiter
                                    value={String(this.props.limit)}
                                    onChange={this.handleLimitChange}/>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-[minmax(180px,1fr)_minmax(260px,1.25fr)_140px_140px] gap-3 items-start">
                                <InputText
                                    name="filterSearch"
                                    value={this.state.filterSearch}
                                    placeholder={i18n('Search')}
                                    icon="search"
                                    onChange={this.handleFilterChange}
                                    onScan={this.handleSearch}/>
                                <DateRangePicker
                                    startAt={this.state.filterStartAt}
                                    endAt={this.state.filterEndAt}
                                    onChange={this.handleDateFilterChange}/>
                                <button
                                    className="bg-primary text-white capitalize focus:outline-none block py-2 px-5 h-input mb-4"
                                    onClick={this.handleSearch}>
                                    {i18n('Search')}
                                </button>
                                <button
                                    className="bg-primary-light text-white capitalize focus:outline-none block py-2 px-5 h-input mb-4"
                                    onClick={this.handleResetFilters}>
                                    {i18n('Reset')}
                                </button>
                            </div>
                            <TableHorizontal
                                headers={this.headers}
                                data={this.props.data}
                                onSelect={this.handleTableSelect}
                                selectedIds={this.state.selectedIds}/>
                            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-end">
                                <p className="text-secondary text-xs mb-1">
                                    {this.status}
                                </p>
                                <Paginator
                                    active={this.props.currentPage}
                                    total={this.props.lastPage}
                                    onPaginate={this.handlePaginate}/>
                            </div>
                        </div>
                        <CardButton cancelText={i18n('Back')}>
                            {this.selectedActions}
                        </CardButton>
                    </Card>
                </ContentMain>
                <ContentMain layout="oneThird">
                    <CustomLabelInfo customLabel={this.state.customLabel}/>
                </ContentMain>
                {this.renderReprintChoice()}
            </ContentLayout>
    )
  }
}

const mapStateToProps = (state: RootState) => {
  const { id, result, limit, search, startAt, endAt } = state.customLabelPrintsPaginate
  const { data, total, from, to, currentPage, lastPage } = result
  return { id, data, total, from, to, currentPage, lastPage, limit, search, startAt, endAt }
}

const mapDispatchToProps = {
  replaceId,
  paginate,
  deletePrints,
  replacePage,
  replaceLimit,
  replaceFilters
}

const connector = connect(mapStateToProps, mapDispatchToProps)

export default connector(withRouter(CustomLabelPrintsPaginate))
